import { lazy, Suspense, useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageProvider } from "@/i18n/LanguageContext";
// Eager: homepage (LCP-critical) + 404 (small, used as fallback)
import Index from "./pages/Index.tsx";
import NotFound from "./pages/NotFound.tsx";
import ScrollDepthTracker from "@/components/ScrollDepthTracker";
import ScrollToTop from "@/components/ScrollToTop";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import { CustomerAuthProvider } from "@/auth/CustomerAuthContext";
import { RequireCustomer } from "@/auth/RequireCustomer";
import { RequireAdmin } from "@/auth/RequireAdmin";

// Lazy-loaded routes — split into per-route chunks
const Privacy = lazy(() => import("./pages/Privacy.tsx"));
const Terms = lazy(() => import("./pages/Terms.tsx"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe.tsx"));
const CompareLanding = lazy(() => import("./pages/CompareLanding.tsx"));
const RequestQuote = lazy(() => import("./pages/RequestQuote.tsx"));
const HowItWorks = lazy(() => import("./pages/HowItWorks.tsx"));
const WhatWeSource = lazy(() => import("./pages/WhatWeSource.tsx"));
const SuppliersHub = lazy(() => import("./pages/SuppliersHub.tsx"));
const SourcingLanding = lazy(() => import("./pages/SourcingLanding.tsx"));
const CountryLanding = lazy(() => import("./pages/CountryLanding.tsx"));
const CollectionsHub = lazy(() => import("./pages/CollectionsHub.tsx"));
const CollectionPage = lazy(() => import("./pages/CollectionPage.tsx"));
const ProductPage = lazy(() => import("./pages/ProductPage.tsx"));
const SolutionsHub = lazy(() => import("./pages/SolutionsHub.tsx"));
const SolutionCollection = lazy(() => import("./pages/SolutionCollection.tsx"));
const SolutionProduct = lazy(() => import("./pages/SolutionProduct.tsx"));
const UseCaseKitPage = lazy(() => import("./pages/UseCaseKitPage.tsx"));
const Sitemap = lazy(() => import("./pages/Sitemap.tsx"));
const Contact = lazy(() => import("./pages/Contact.tsx"));
const About = lazy(() => import("./pages/About.tsx"));
const Industries = lazy(() => import("./pages/Industries.tsx"));
const CaseStudies = lazy(() => import("./pages/CaseStudies.tsx"));
const PowerSystems = lazy(() => import("./pages/PowerSystems.tsx"));
const PowerSystemProduct = lazy(() => import("./pages/PowerSystemProduct.tsx"));
const AdminEmails = lazy(() => import("./pages/AdminEmails.tsx"));
const AdminAuth = lazy(() => import("./pages/AdminAuth.tsx"));
const AdminResetPassword = lazy(() => import("./pages/AdminResetPassword.tsx"));
const AdminQuotes = lazy(() => import("./pages/AdminQuotes.tsx"));
const AdminRequests = lazy(() => import("./pages/AdminRequests.tsx"));
const AdminRequestSourcing = lazy(() => import("./pages/AdminRequestSourcing.tsx"));
const AdminConcierge = lazy(() => import("./pages/AdminConcierge.tsx"));
const AdminQuotesV2 = lazy(() => import("./pages/AdminQuotesV2.tsx"));
const AdminOrders = lazy(() => import("./pages/AdminOrders.tsx"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard.tsx"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics.tsx"));
const AdminPriorityAccounts = lazy(() => import("./pages/AdminPriorityAccounts.tsx"));
const AdminPrioritySkuFamilies = lazy(() => import("./pages/AdminPrioritySkuFamilies.tsx"));
const AdminPreferredSuppliers = lazy(() => import("./pages/AdminPreferredSuppliers.tsx"));
const AdminSupplierScoring = lazy(() => import("./pages/AdminSupplierScoring.tsx"));
const AdminConcentration = lazy(() => import("./pages/AdminConcentration.tsx"));
const AdminAccounts = lazy(() => import("./pages/AdminAccounts.tsx"));
const AdminLeads = lazy(() => import("./pages/AdminLeads.tsx"));
const AdminSupplierApplications = lazy(() => import("./pages/AdminSupplierApplications.tsx"));
const AdminSupplierIntegrations = lazy(() => import("./pages/AdminSupplierIntegrations.tsx"));
const AdminOperatorOverrides = lazy(() => import("./pages/AdminOperatorOverrides.tsx"));
const AdminFailures = lazy(() => import("./pages/AdminFailures.tsx"));
const AdminPricingRisk = lazy(() => import("./pages/AdminPricingRisk.tsx"));
const AdminPrescriptiveRules = lazy(() => import("./pages/AdminPrescriptiveRules.tsx"));
const AdminSystemEvents = lazy(() => import("./pages/AdminSystemEvents.tsx"));
const AdminAutomation = lazy(() => import("./pages/AdminAutomation.tsx"));
const AdminAlerts = lazy(() => import("./pages/AdminAlerts.tsx"));
const AdminSystemErrors = lazy(() => import("./pages/AdminSystemErrors.tsx"));
const AdminSourceChecks = lazy(() => import("./pages/AdminSourceChecks.tsx"));
const AdminQaSandbox = lazy(() => import("./pages/AdminQaSandbox.tsx"));
const AdminLogistics = lazy(() => import("./pages/AdminLogistics.tsx"));
const AdminLogisticsIntegrations = lazy(() => import("./pages/AdminLogisticsIntegrations.tsx"));
const Blog = lazy(() => import("./pages/Blog.tsx"));
const BlogPost = lazy(() => import("./pages/BlogPost.tsx"));
const AccountAuth = lazy(() => import("./pages/AccountAuth.tsx"));
const AccountResetPassword = lazy(() => import("./pages/AccountResetPassword.tsx"));
const AccountOrders = lazy(() => import("./pages/AccountOrders.tsx"));
const AccountOrdersList = lazy(() => import("./pages/AccountOrdersList.tsx"));
const AccountQuotes = lazy(() => import("./pages/AccountQuotes.tsx"));
const AccountProfile = lazy(() => import("./pages/AccountProfile.tsx"));
const AccountBom = lazy(() => import("./pages/AccountBom.tsx"));
const AccountPayments = lazy(() => import("./pages/AccountPayments.tsx"));
const SeoLanding = lazy(() => import("./pages/SeoLanding.tsx"));
const SwitchgearLanding = lazy(() => import("./pages/SwitchgearLanding.tsx"));
const SupplierPartners = lazy(() => import("./pages/SupplierPartners.tsx"));
const SupplierAuth = lazy(() => import("./pages/SupplierAuth.tsx"));
const SupplierPortal = lazy(() => import("./pages/SupplierPortal.tsx"));
const OutOfStockLanding = lazy(() => import("./pages/OutOfStockLanding.tsx"));
const InstantSourceCheck = lazy(() => import("./pages/InstantSourceCheck.tsx"));
const SwitchgearSourcingLatamLanding = lazy(() => import("./pages/SwitchgearSourcingLatamLanding.tsx"));
const QuoteReview = lazy(() => import("./pages/QuoteReview.tsx"));
import { SEO_LANDING_SLUGS } from "@/data/seoLandings";
import { LANDING_PAGE_SLUGS } from "@/data/landingPages";

// Sonner pulls a separate toast library into the bundle. Only the admin
// surface uses sonner directly today; the public site uses the radix-based
// `Toaster` above. Lazy-load Sonner after idle / first interaction so it
// stays off the LCP critical path while still being mounted before any
// admin route or RFQ success toast can fire.
const SonnerToaster = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })),
);

const DeferredSonner = () => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let done = false;
    const ready_ = () => {
      if (done) return;
      done = true;
      setReady(true);
    };
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    if (typeof w.requestIdleCallback === "function") {
      w.requestIdleCallback(ready_, { timeout: 3000 });
    } else {
      window.setTimeout(ready_, 1500);
    }
    const events: Array<keyof WindowEventMap> = ["pointerdown", "keydown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, ready_, { once: true, passive: true }));
    return () => {
      events.forEach((e) => window.removeEventListener(e, ready_));
    };
  }, []);
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <SonnerToaster />
    </Suspense>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/** Preserves locale prefix + dynamic params when 301-redirecting legacy
 *  `/solutions/*` paths to the canonical base. */
const LegacySolutionsRedirect = () => {
  const { pathname, search, hash } = useLocation();
  const target = pathname.replace(
    /(^|\/)solutions(\/|$)/,
    "$1pre-configured-power-packages$2",
  );
  return <Navigate to={`${target}${search}${hash}`} replace />;
};

/** Preserves locale prefix + dynamic params when 301-redirecting the
 *  previous `/pre-configured-kits/*` paths to the new canonical base. */
const LegacyKitsRedirect = () => {
  const { pathname, search, hash } = useLocation();
  const target = pathname.replace(
    /(^|\/)pre-configured-kits(\/|$)/,
    "$1pre-configured-power-packages$2",
  );
  return <Navigate to={`${target}${search}${hash}`} replace />;
};

/** Preserves locale prefix + dynamic params when 301-redirecting the
 *  previous `/collections/*` paths to the canonical
 *  `/fast-access-power-components/*` base. */
const LegacyCollectionsRedirect = () => {
  const { pathname, search, hash } = useLocation();
  const target = pathname.replace(
    /(^|\/)collections(\/|$)/,
    "$1fast-access-power-components$2",
  );
  return <Navigate to={`${target}${search}${hash}`} replace />;
};

/** Preserves locale prefix when 301-redirecting the legacy
 *  `/instant-source-check` route to the canonical `/ask-switzh`. */
const LegacyInstantSourceCheckRedirect = () => {
  const { pathname, search, hash } = useLocation();
  const target = pathname.replace(
    /(^|\/)instant-source-check(\/|$)/,
    "$1ask-switzh$2",
  );
  return <Navigate to={`${target}${search}${hash}`} replace />;
};

/** Minimal fallback while a lazy route chunk is fetched. Keeps layout stable. */
const RouteFallback = () => (
  <div className="min-h-screen bg-background" aria-hidden="true" />
);

const LocalizedRoutes = () => (
  <LanguageProvider>
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route index element={<Index />} />
        <Route path="privacy" element={<Privacy />} />
        <Route path="terms" element={<Terms />} />
        <Route path="unsubscribe" element={<Unsubscribe />} />
        <Route path="compare" element={<CompareLanding />} />
        <Route path="request-quote" element={<RequestQuote />} />
        <Route path="how-it-works" element={<HowItWorks />} />
        <Route path="ask-switzh" element={<InstantSourceCheck />} />
        {/* Legacy /instant-source-check URL — 301-style client redirect to /ask-switzh (locale-aware) */}
        <Route path="instant-source-check" element={<LegacyInstantSourceCheckRedirect />} />
        <Route path="what-we-source" element={<WhatWeSource />} />
        <Route path="suppliers" element={<SuppliersHub />} />
        <Route path="suppliers/:country/:brand/:product" element={<SourcingLanding />} />
        <Route path="fast-access-power-components" element={<CollectionsHub />} />
        <Route path="fast-access-power-components/:slug" element={<CollectionPage />} />
        <Route path="product/:slug" element={<ProductPage />} />
        {/* Legacy /collections URLs — 301-style client redirect to canonical base */}
        <Route path="collections" element={<Navigate to="/fast-access-power-components" replace />} />
        <Route path="collections/:slug" element={<LegacyCollectionsRedirect />} />
        <Route path="pre-configured-power-packages" element={<SolutionsHub />} />
        <Route path="pre-configured-power-packages/kits/:kitSlug" element={<UseCaseKitPage />} />
        <Route path="pre-configured-power-packages/:collection" element={<SolutionCollection />} />
        <Route path="pre-configured-power-packages/:collection/:slug" element={<SolutionProduct />} />
        {/* Legacy /pre-configured-kits URLs — 301-style client redirect to /pre-configured-power-packages */}
        <Route path="pre-configured-kits" element={<Navigate to="/pre-configured-power-packages" replace />} />
        <Route path="pre-configured-kits/:collection" element={<LegacyKitsRedirect />} />
        <Route path="pre-configured-kits/:collection/:slug" element={<LegacyKitsRedirect />} />
        {/* Legacy /solutions URLs — 301-style client redirect to canonical base */}
        <Route path="solutions" element={<Navigate to="/pre-configured-power-packages" replace />} />
        <Route path="solutions/:collection" element={<LegacySolutionsRedirect />} />
        <Route path="solutions/:collection/:slug" element={<LegacySolutionsRedirect />} />
        <Route path="sitemap" element={<Sitemap />} />
        <Route path="contact" element={<Contact />} />
        <Route path="about" element={<About />} />
        <Route path="industries" element={<Industries />} />
        <Route path="case-studies" element={<CaseStudies />} />
        <Route path="power-systems" element={<PowerSystems />} />
        <Route path="power-systems/:slug" element={<PowerSystemProduct />} />
        <Route path="blog" element={<Blog />} />
        <Route path="blog/:slug" element={<BlogPost />} />
        {/* Supplier partner application — standalone, indexable, not in nav. */}
        <Route path="supplier-partners" element={<SupplierPartners />} />
        {/* Supplier auth + portal — gated by `supplier` role inside the page. */}
        <Route path="supplier-login" element={<SupplierAuth />} />
        <Route path="supplier-portal" element={<SupplierPortal />} />
        {/* Spec-canonical alias — /supplier-dashboard mirrors /supplier-portal
            so external links and docs can use the more conventional URL.
            Same auth/role gating happens inside SupplierPortal. */}
        <Route path="supplier-dashboard" element={<SupplierPortal />} />
        {/* Standalone high-conversion landing — urgent out-of-stock buyers (LATAM).
            NOT linked from the global header or footer. Indexable via sitemap. */}
        <Route
          path="out-of-stock-electrical-components-latam"
          element={<OutOfStockLanding />}
        />
        {/* High-conversion landing — switchgear sourcing for LATAM buyers.
            Standalone, indexable, intentionally not linked from primary nav. */}
        <Route
          path="switchgear-sourcing-latam"
          element={<SwitchgearSourcingLatamLanding />}
        />
        {/* High-intent SEO landing pages — standalone, indexable, not in nav. */}
        {/* Permanent redirect: deprecated supplier slug consolidated into the canonical
            /electrical-components-supplier-latam-usa to eliminate duplication. */}
        <Route
          path="electrical-components-supplier-usa-latam"
          element={<Navigate to="/electrical-components-supplier-latam-usa" replace />}
        />
        {SEO_LANDING_SLUGS.map((slug) => (
          <Route
            key={slug}
            path={slug}
            element={<SeoLanding slug={slug} />}
          />
        ))}
        {/* Hybrid /lp/* switchgear landing pages — brand + use-case anchors,
            sharper conversion-focused template than SeoLanding above.
            Standalone, indexable, intentionally not in primary nav. */}
        {LANDING_PAGE_SLUGS.map((slug) => (
          <Route key={`lp-${slug}`} path={`lp/${slug}`} element={<SwitchgearLanding />} />
        ))}
        {/* Country landing pages — geo-intent (must be listed explicitly to avoid clobbering other routes) */}
        <Route path="colombia" element={<CountryLanding />} />
        <Route path="mexico" element={<CountryLanding />} />
        <Route path="peru" element={<CountryLanding />} />
        <Route path="chile" element={<CountryLanding />} />
        <Route path="brazil" element={<CountryLanding />} />
        <Route path="argentina" element={<CountryLanding />} />
        {/* Customer account — guest-first; auth optional, never required for quoting */}
        <Route path="account/auth" element={<AccountAuth />} />
        <Route path="account/reset-password" element={<AccountResetPassword />} />
        <Route
          path="account"
          element={
            <RequireCustomer>
              <AccountOrders />
            </RequireCustomer>
          }
        />
        <Route
          path="account/quotes"
          element={
            <RequireCustomer>
              <AccountQuotes />
            </RequireCustomer>
          }
        />
        <Route
          path="account/orders"
          element={
            <RequireCustomer>
              <AccountOrdersList />
            </RequireCustomer>
          }
        />
        <Route
          path="account/profile"
          element={
            <RequireCustomer>
              <AccountProfile />
            </RequireCustomer>
          }
        />
        <Route
          path="account/bom"
          element={
            <RequireCustomer>
              <AccountBom />
            </RequireCustomer>
          }
        />
        <Route
          path="account/payments"
          element={
            <RequireCustomer>
              <AccountPayments />
            </RequireCustomer>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  </LanguageProvider>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <DeferredSonner />
      <BrowserRouter>
        <CustomerAuthProvider>
          <ScrollToTop />
          <ScrollDepthTracker />
          <AnalyticsTracker />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
            {/* Admin (no language wrapper, no localized prefixes) */}
            <Route path="/admin/auth" element={<AdminAuth />} />
            {/* Public tokenized buyer quote review — no auth required, token-gated via SECURITY DEFINER RPC */}
            <Route path="/quote/review/:token" element={<QuoteReview />} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />
            <Route path="/admin/emails" element={<RequireAdmin><AdminEmails /></RequireAdmin>} />
            <Route path="/admin/quotes" element={<RequireAdmin><AdminQuotes /></RequireAdmin>} />
            <Route path="/admin/requests" element={<RequireAdmin><AdminRequests /></RequireAdmin>} />
            <Route path="/admin/requests/:id/sourcing" element={<RequireAdmin><AdminRequestSourcing /></RequireAdmin>} />
            <Route path="/admin/concierge" element={<RequireAdmin><AdminConcierge /></RequireAdmin>} />
            <Route path="/admin/quotes-v2" element={<RequireAdmin><AdminQuotesV2 /></RequireAdmin>} />
            <Route path="/admin/orders" element={<RequireAdmin><AdminOrders /></RequireAdmin>} />
            <Route path="/admin/analytics" element={<RequireAdmin><AdminAnalytics /></RequireAdmin>} />
            <Route path="/admin/priority-accounts" element={<RequireAdmin><AdminPriorityAccounts /></RequireAdmin>} />
            <Route path="/admin/priority-sku-families" element={<RequireAdmin><AdminPrioritySkuFamilies /></RequireAdmin>} />
            <Route path="/admin/preferred-suppliers" element={<RequireAdmin><AdminPreferredSuppliers /></RequireAdmin>} />
            <Route path="/admin/supplier-scoring" element={<RequireAdmin><AdminSupplierScoring /></RequireAdmin>} />
            <Route path="/admin/concentration" element={<RequireAdmin><AdminConcentration /></RequireAdmin>} />
            <Route path="/admin/accounts" element={<RequireAdmin><AdminAccounts /></RequireAdmin>} />
            <Route path="/admin/leads" element={<RequireAdmin><AdminLeads /></RequireAdmin>} />
            <Route path="/admin/supplier-requests" element={<RequireAdmin><AdminSupplierApplications /></RequireAdmin>} />
            <Route path="/admin/supplier-integrations" element={<RequireAdmin><AdminSupplierIntegrations /></RequireAdmin>} />
            <Route path="/admin/overrides" element={<RequireAdmin><AdminOperatorOverrides /></RequireAdmin>} />
            <Route path="/admin/failures" element={<RequireAdmin><AdminFailures /></RequireAdmin>} />
            <Route path="/admin/pricing-risk" element={<RequireAdmin><AdminPricingRisk /></RequireAdmin>} />
            <Route path="/admin/prescriptive-rules" element={<RequireAdmin><AdminPrescriptiveRules /></RequireAdmin>} />
            <Route path="/admin/system-events" element={<RequireAdmin><AdminSystemEvents /></RequireAdmin>} />
            <Route path="/admin/automation" element={<RequireAdmin><AdminAutomation /></RequireAdmin>} />
            <Route path="/admin/alerts" element={<RequireAdmin><AdminAlerts /></RequireAdmin>} />
            <Route path="/admin/system-errors" element={<RequireAdmin><AdminSystemErrors /></RequireAdmin>} />
            <Route path="/admin/source-checks" element={<RequireAdmin><AdminSourceChecks /></RequireAdmin>} />
            <Route path="/admin/qa-sandbox" element={<RequireAdmin><AdminQaSandbox /></RequireAdmin>} />
            <Route path="/admin/logistics" element={<RequireAdmin><AdminLogistics /></RequireAdmin>} />
            <Route path="/admin/logistics-integrations" element={<RequireAdmin><AdminLogisticsIntegrations /></RequireAdmin>} />
            <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
            {/* Localized routes for ES and PT */}
            <Route path="/es/*" element={<LocalizedRoutes />} />
            <Route path="/pt/*" element={<LocalizedRoutes />} />
            {/* Default English (no prefix) */}
            <Route path="/*" element={<LocalizedRoutes />} />
            </Routes>
          </Suspense>
        </CustomerAuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
